// @ts-strict-ignore
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ValidatorFactory } from '@shared/validator.factory';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ConnectionAreaVO, MetaElementVO, SelectionMetaElementUsageIdentity, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { CustomValidators } from '@shared/custom-validators';
import { ProjectModel } from '@project/model/project.model';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ConnectionAreaType, ConnectionCreationParameters, ConnectionCreator, ConnectionShapeType } from '@article/components/connections/connection-creation/connection-creator';
import { ArrayUtils } from '@shared/util/array-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { AddConnectionOperation } from '../../../operation/add-connection-operation';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { Vector3 } from 'three';
import { AdminService } from '../../../services/admin.service';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { AdmintoolOperationFactory } from '../../../operation/admintool-operation.factory';

@Component({
  standalone: false,
  selector: 'app-new-connection-popup',
  templateUrl: './new-connection-modal.component.html',
})
export class NewConnectionModalComponent implements OnInit {
  public readonly ShapeType = ConnectionShapeType;

  @Input() public articleVO: ArticleVO;
  @Output() public finish = new EventEmitter<void>();
  @Input() public articleContainer: ArticleContainer;
  @Input() public connectionParameters: ConnectionCreationParameters;

  public connectionColor: string = '#ffffff';
  private _imageFile: any;
  private bitmapData: HTMLImageElement;
  public imageUrl: any;
  public imageContainerWidth: number = 0;
  public imageContainerHeight: number = 0;

  public connectionNamePrefix: string = MetaElementVO.CONNECTION_PREFIX;

  public connectionTypes: string[] = [ConnectionAreaType.CONNECTION_AREA, ConnectionAreaType.TEXTURE_AREA, ConnectionAreaType.MULTIPURPOSE_AREA];
  public sideTypes: string[] = [ConnectionAreaVO.SIDE_ACCEPTING, ConnectionAreaVO.SIDE_GIVING];
  public textureTypes: string[] = [TextureAreaVO.TYPE_TEXT, TextureAreaVO.TYPE_TEXT_OR_IMAGE_USAGE, TextureAreaVO.TYPE_IMAGE];

  public form: FormGroup;

  constructor(
    private cdRef: ChangeDetectorRef,
    private fb: FormBuilder,
    private bsModalRef: BsModalRef,
    private alertHelper: AlertHelper,
    private selectionModelService: SelectionModelService,
    private localeResourceManager: LocaleResourceManager,
    private projectModel: ProjectModel,
    private undoRedoHelper: UndoRedoHelper,
    private connectionCreator: ConnectionCreator,
    private adminService: AdminService,
    private applicationRefreshService: ApplicationRefreshService,
    private admintoolOperationFactory: AdmintoolOperationFactory,
    private validatorFactory: ValidatorFactory,
  ) {}

  public ngOnInit(): void {
    const sizeValidator = this.validatorFactory.number({min: 0, precision: 3, allowNegative: false});
    const positionRotationValidator = this.validatorFactory.number({precision: 3});
    const multiplicityValidator = this.validatorFactory.number({min: 0, allowNegative: false});

    if (this.connectionParameters && this.connectionParameters.connectionName && this.connectionParameters.connectionName.length > 2) {
      this.form = this.fb.group({
        autoGenerateConnectionName: [false],
        connectionName: [
          { value: this.connectionParameters.connectionName.substring(2), disabled: true },
          [this.connectionNameValidate.bind(this), CustomValidators.trimRequired],
        ],
        connectionType: [this.connectionParameters.connectionType],
        connectionSide: [
          this.connectionParameters.connectionType !== ConnectionAreaType.TEXTURE_AREA ? this.connectionParameters.connectionInfo : this.sideTypes[0],
        ],
        textureType: [
          this.connectionParameters.connectionType === ConnectionAreaType.TEXTURE_AREA
            ? this.connectionParameters.connectionInfo
            : this.textureTypes[0],
        ],
        connectionShapeType: [this.connectionParameters.connectionForm],
        connectionLength: [
          this.localeResourceManager.formatNumber(MathUtils.roundNumberValue(this.connectionParameters.connectionLength, 3)),
          [sizeValidator, CustomValidators.trimRequired],
        ],
        connectionWidth: [
          this.localeResourceManager.formatNumber(MathUtils.roundNumberValue(this.connectionParameters.connectionWidth, 3)),
          [sizeValidator, CustomValidators.trimRequired],
        ],
        connectionRadius: [
          this.localeResourceManager.formatNumber(MathUtils.roundNumberValue(this.connectionParameters.connectionRadius, 3)),
          [sizeValidator, CustomValidators.trimRequired],
        ],
        connectionMultiplicity: [
          NumberUtils.isNumber(this.connectionParameters.connectionMultiplicity)
            ? this.localeResourceManager.formatNumber(this.connectionParameters.connectionMultiplicity)
            : '1',
          [multiplicityValidator],
        ],
        determinePosition: [!!this.connectionParameters.metaElement],
        positionX: [
          this.localeResourceManager.formatNumber(MathUtils.roundNumberValue(this.connectionParameters.position.x, 3)),
          [positionRotationValidator, CustomValidators.trimRequired],
        ],
        positionY: [
          this.localeResourceManager.formatNumber(MathUtils.roundNumberValue(this.connectionParameters.position.y, 3)),
          [positionRotationValidator, CustomValidators.trimRequired],
        ],
        positionZ: [
          this.localeResourceManager.formatNumber(MathUtils.roundNumberValue(this.connectionParameters.position.z, 3)),
          [positionRotationValidator, CustomValidators.trimRequired],
        ],
        determineRotation: [!!this.connectionParameters.metaElement],
        rotationX: [
          this.localeResourceManager.formatNumber(MathUtils.roundNumberValue(this.connectionParameters.rotation.x, 3)),
          [positionRotationValidator, CustomValidators.trimRequired],
        ],
        rotationY: [
          this.localeResourceManager.formatNumber(MathUtils.roundNumberValue(this.connectionParameters.rotation.y, 3)),
          [positionRotationValidator, CustomValidators.trimRequired],
        ],
        rotationZ: [
          this.localeResourceManager.formatNumber(MathUtils.roundNumberValue(this.connectionParameters.rotation.z, 3)),
          [positionRotationValidator, CustomValidators.trimRequired],
        ],
        copyProperties: { value: !!this.connectionParameters.metaElement, disabled: !this.connectionParameters.metaElement },
        connectionDoubleside: [!!this.connectionParameters.connectionDoubleside],
      });
      this.connectionColor = this.connectionParameters.connectionColor;
      if (this.connectionParameters.bitmapData) {
        this.bitmapData = this.connectionParameters.bitmapData;
        this.imageUrl = this.bitmapData.src;
        this.resizeImageContainer(this.bitmapData.width, this.bitmapData.height);
      }
    } else {
      this.form = this.fb.group({
        autoGenerateConnectionName: [false],
        connectionName: [{ value: '', disabled: true }, [this.connectionNameValidate.bind(this), CustomValidators.trimRequired]],
        connectionType: [this.connectionTypes[0]],
        connectionSide: [this.sideTypes[0]],
        textureType: [this.textureTypes[0]],
        connectionShapeType: [this.ShapeType.RECTANGLE],
        connectionLength: ['10', [sizeValidator, CustomValidators.trimRequired]],
        connectionWidth: ['10', [sizeValidator, CustomValidators.trimRequired]],
        connectionRadius: ['10', [sizeValidator, CustomValidators.trimRequired]],
        connectionMultiplicity: ['1', [multiplicityValidator]],
        determinePosition: [false],
        positionX: ['0', [positionRotationValidator, CustomValidators.trimRequired]],
        positionY: ['0', [positionRotationValidator, CustomValidators.trimRequired]],
        positionZ: ['0', [positionRotationValidator, CustomValidators.trimRequired]],
        determineRotation: [false],
        rotationX: ['0', [positionRotationValidator, CustomValidators.trimRequired]],
        rotationY: ['0', [positionRotationValidator, CustomValidators.trimRequired]],
        rotationZ: ['0', [positionRotationValidator, CustomValidators.trimRequired]],
        connectionDoubleside: [false],
      });
    }
    this.autoGenerateConnectionNameHandler();
  }

  public close(): void {
    this.bsModalRef.hide();
    this.finish.emit();
  }

  public refreshView(): void {
    this.cdRef.detectChanges();
  }

  private generateNewConnectionName(): string {
    let nameCounter: number = 1;
    while (!this.testConnectionName(nameCounter.toString())) {
      nameCounter++;
    }
    return nameCounter.toString();
  }

  // Do not give Connection name with prefix
  public testConnectionName(testName: string): boolean {
    testName = MetaElementVO.CONNECTION_PREFIX + testName;
    const connections = this.articleVO.metaElements;
    for (let connection of connections) {
      if (connection?.name === testName) {
        return false;
      }
    }
    return true;
  }

  public autoGenerateConnectionNameHandler(): void {
    if (this.form.get('autoGenerateConnectionName').value) {
      this.form.get('connectionName').setValue(this.generateNewConnectionName());
      this.form.get('connectionName').disable();
    } else {
      this.form.get('connectionName').enable();
    }
  }

  public connectionNameValidate(c: FormControl): ValidationErrors | null {
    if (this.form.get('autoGenerateConnectionName').value) {
      return;
    }
    if (c.value.disabled) {
      return;
    }
    const value = MetaElementVO.CONNECTION_PREFIX + c.value.trim() || ``;
    if (!MeshUtils.isConnection(value)) {
      return { messageKey: `newConnectionPopup.name.hint` };
    }
    for (const connection of this.articleVO.metaElements) {
      if (connection.name === value) {
        return { messageKey: `admintool.error.doubleValueNotAllowed` };
      }
    }
  }

  public getInputError(c: AbstractControl): string {
    if (c && c.hasError('messageKey')) {
      return this.localeResourceManager.getValueByKey(c.getError('messageKey'), { 0: c.getError('param') });
    } else {
      return '';
    }
  }

  public isSubmitAvailable(): boolean {
    return this.form.valid && this.form.get('connectionType').value && this.form.get('connectionShapeType').value;
  }

  private getConnectionInfo(): string {
    let connectionInfo: string;
    const connectionType = this.form.get('connectionType').value;
    if (connectionType === ConnectionAreaType.TEXTURE_AREA) {
      connectionInfo = this.form.get('textureType').value;
    } else if (connectionType === ConnectionAreaType.CONNECTION_AREA) {
      connectionInfo = this.form.get('connectionSide').value;
    }
    return connectionInfo;
  }

  public async save(): Promise<void> {
    const selectedForm: string = this.form.get('connectionShapeType').value;
    const selectedType: string = this.form.get('connectionType').value;
    const selectedConnectionInfo: string = this.getConnectionInfo();
    const connectionName: string =
      MetaElementVO.CONNECTION_PREFIX +
      (this.form.get('autoGenerateConnectionName').value ? this.generateNewConnectionName() : this.form.get('connectionName').value.trim());

    const connectionMultiplicity: number = this.localeResourceManager.parseNumber(this.form.get('connectionMultiplicity').value);
    const connectionLength: number = this.localeResourceManager.parseNumber(this.form.get('connectionLength').value);
    const connectionWidth: number = this.localeResourceManager.parseNumber(this.form.get('connectionWidth').value);
    const connectionRadius: number = this.localeResourceManager.parseNumber(this.form.get('connectionRadius').value);
    const determinePosition: boolean = this.form.get('determinePosition').value;
    const determineRotation: boolean = this.form.get('determineRotation').value;
    const position: Vector3 = new Vector3(
      this.localeResourceManager.parseNumber(this.form.get('positionX').value),
      this.localeResourceManager.parseNumber(this.form.get('positionY').value),
      this.localeResourceManager.parseNumber(this.form.get('positionZ').value)
    );
    const rotation: Vector3 = new Vector3(
      this.localeResourceManager.parseNumber(this.form.get('rotationX').value),
      this.localeResourceManager.parseNumber(this.form.get('rotationY').value),
      this.localeResourceManager.parseNumber(this.form.get('rotationZ').value)
    );

    const connection: Connection = this.connectionCreator.createConnection(
      {
        connectionForm: selectedForm,
        connectionType: selectedType,
        connectionInfo: selectedConnectionInfo,
        connectionName: connectionName,
        connectionMultiplicity: connectionMultiplicity,
        connectionDoubleside: this.form.get('connectionDoubleside').value,
        connectionLength: connectionLength,
        connectionWidth: connectionWidth,
        connectionRadius: connectionRadius,
        bitmapData: this.bitmapData,
        connectionColor: this.connectionColor,
        position: determinePosition ? position : null,
        rotation: determineRotation ? rotation : null,
        metaElement: this.form.get('copyProperties') && this.form.get('copyProperties').value ? this.connectionParameters.metaElement : null,
      },
      this.articleContainer,
      this.articleVO
    );

    this.applicationRefreshService.notifyProjectChange();
    this.projectModel.notifyCurrentProjectChanged();
    if (connection) {
      const connectionSelectionIdentity: SelectionMetaElementUsageIdentity = new SelectionMetaElementUsageIdentity(
        this.articleContainer.articleGUID,
        connection
      );
      this.selectionModelService.selectObject(connectionSelectionIdentity, false, false);
      this.articleContainer.pickConnectorPositionMode = !determinePosition;
      this.articleContainer.pickConnectorRotationMode = !determineRotation;

      const connectionAttachOperation: AddConnectionOperation = this.admintoolOperationFactory.createConnectionAddOperation(
        connection, this.articleContainer, this.articleVO
      );
      if (connectionAttachOperation) {
        await this.undoRedoHelper.pushUndoOperation(connectionAttachOperation);
      }
      if (!determinePosition || !determineRotation) {
        this.adminService.togglePositionCursor(null);
      }
    }
    this.close();
  }

  public imageUpload(e) {
    const reader: FileReader = new FileReader();
    // get the selected file from event
    reader.onloadend = (event) => {
      const img = new Image();
      img.onload = () => {
        if (img.width > ConnectionCreator.MAX_ALLOWED_IMAGE_WIDTH || img.height > ConnectionCreator.MAX_ALLOWED_IMAGE_HEIGHT) {
          this.alertHelper.showError('imageLoader.error.title.image_too_big', 'imageLoader.error.message.image_too_big');
          return;
        } else {
          // Assign the result to variable for setting the src of image element
          this.imageUrl = reader.result;
          this.bitmapData = img;
          if (img) {
            this.resizeImageContainer(img.width, img.height);
          }
        }
      };
      img.src = reader.result as string;
    };
    if (ArrayUtils.isNotEmpty(e.target.files) && /(png|jpg|jpeg)/.test(e.target.files[0].type)) {
      // only png, jpg, jpeg types are allowed
      this._imageFile = e.target.files[0];
      reader.readAsDataURL(this._imageFile);
    }
  }

  private resizeImageContainer(widthImage: number, heightImage: number): void {
    if (widthImage && heightImage) {
      const differenceWidth = widthImage / ConnectionCreator.MAX_WIDTH_CONTAINER;
      const differenceHeight = heightImage / ConnectionCreator.MAX_HEIGHT_CONTAINER;
      const maxDifference: number = Math.max(differenceWidth, differenceHeight);
      this.imageContainerWidth = Math.ceil(widthImage / maxDifference);
      this.imageContainerHeight = Math.ceil(heightImage / maxDifference);
    }
  }
}
